// The Scholars' deduction: the world's eight PRINCIPLES each have a hidden
// NATURE - hot or cold, wet or dry, heavy or light - dealt fresh each game.
// Mixing two principles shows what they SHARE. A technology is a REACTION:
// find the pair that produces it and it is learned. Publish a theory about
// a principle's nature; the night holds it up or debunks it.
//
// Matt: "Alchemists is one of my very favorite games." Pure helpers over
// `civ.lore`; the phone does the bookkeeping (see consistentNatures).

import { shuffle, nextInt } from './rng/rng';

export const PRINCIPLES = ['fire', 'water', 'stone', 'wind', 'growth', 'metal', 'number', 'word'];
// (The second bench of eight "deep" elements is gone, 2026-10-15: Matt,
// "it seems like I just have another scramble of the exact same puzzle".
// Above the eight, the ladder of machines in machines.js takes over.)
export const ALL_PRINCIPLES = PRINCIPLES;
export const PRINCIPLE_NAMES = {
  fire: 'Fire', water: 'Water', stone: 'Stone', wind: 'Wind', growth: 'Growth', metal: 'Metal', number: 'Number', word: 'Word',
};
export const ASPECTS = ['heat', 'moisture', 'weight'];
export const ASPECT_WORDS = {
  heat: { true: 'hot', false: 'cold' },
  moisture: { true: 'wet', false: 'dry' },
  weight: { true: 'heavy', false: 'light' },
};
export const aspectWord = (aspect, value) => ASPECT_WORDS[aspect][String(value)];

// The eight natures: every combination of the three aspects.
export const NATURES = [];
[true, false].forEach((heat) => [true, false].forEach((moisture) => [true, false].forEach((weight) => NATURES.push({ heat, moisture, weight }))));
export const natureKey = (n) => `${n.heat ? 'h' : 'c'}${n.moisture ? 'w' : 'd'}${n.weight ? 'H' : 'L'}`;
export const describeNature = (n) => `${aspectWord('heat', n.heat)}, ${aspectWord('moisture', n.moisture)}, ${aspectWord('weight', n.weight)}`;
export const sameNature = (a, b) => ASPECTS.every((k) => a[k] === b[k]);

/** What two natures share: the aspects where they agree, with the value. */
export const shared = (a, b) => ASPECTS.filter((k) => a[k] === b[k]).reduce((acc, k) => ({ ...acc, [k]: a[k] }), {});
/** "hot" / "hot and wet" / "hot, wet, and heavy". */
export const joinWords = (parts) => {
  if (parts.length < 3) return parts.join(' and ');
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};
export const describeShared = (s) => {
  const parts = ASPECTS.filter((k) => k in s).map((k) => aspectWord(k, s[k]));
  return parts.length ? joinWords(parts) : 'no reaction';
};

/** Deal the natures and the reactions for this game. Returns [lore, rng]. */
/** One technology's reaction, dealt the same way startLore deals them. Returns [need, rng]. */
const dealReaction = (t, reactions, rng) => {
  let need = {};
  const parents = (t.needs || []).filter((n) => reactions[n]);
  if (parents.length) {
    let pi;
    [pi, rng] = nextInt(parents.length, rng);
    const from = reactions[parents[pi]];
    const keys = Object.keys(from);
    let ki;
    [ki, rng] = nextInt(keys.length, rng);
    need[keys[ki]] = from[keys[ki]];
  }
  const want = t.tier === 1 ? 1 : 2;
  while (Object.keys(need).length < want) {
    let ai; let v;
    [ai, rng] = nextInt(ASPECTS.length, rng);
    [v, rng] = nextInt(2, rng);
    if (!(ASPECTS[ai] in need)) need = { ...need, [ASPECTS[ai]]: v === 1 };
  }
  return [need, rng];
};

/** Any technology with no reaction yet (new to the tree, or an old save) gets one. Returns [civ, rng]. */
export const dealMissingReactions = (civ, techs, rng) => {
  const lore = civ?.lore;
  if (!lore?.reactions) return [civ, rng];
  const missing = Object.keys(techs).filter((id) => !lore.reactions[id]).sort((a, b) => techs[a].tier - techs[b].tier);
  if (!missing.length) return [civ, rng];
  const reactions = { ...lore.reactions };
  for (const id of missing) {
    let need;
    [need, rng] = dealReaction(techs[id], reactions, rng);
    reactions[id] = need;
  }
  return [{ ...civ, lore: { ...lore, reactions } }, rng];
};

export const startLore = (rng, techs) => {
  let dealt;
  [dealt, rng] = shuffle(NATURES, rng);
  const natures = Object.fromEntries(PRINCIPLES.map((p, i) => [p, dealt[i]]));
  // The machines' laws are dealt at the END of createGameState
  // (dealMissingMachines), after everything else the seed decides.
  // Reactions: tier 1 needs one aspect; deeper techs need two, one of
  // them shared with a prerequisite (the tree is a clue).
  const reactions = {};
  // Techs marked `late` (added after games were already being played) are
  // dealt at the very end by dealMissingReactions, so older seeds keep theirs.
  const ids = Object.keys(techs).filter((id) => !techs[id].late).sort((a, b) => techs[a].tier - techs[b].tier);
  for (const id of ids) {
    const t = techs[id];
    let need = {};
    const parents = (t.needs || []).filter((n) => reactions[n]);
    if (parents.length) {
      let pi;
      [pi, rng] = nextInt(parents.length, rng);
      const from = reactions[parents[pi]];
      const keys = Object.keys(from);
      let ki;
      [ki, rng] = nextInt(keys.length, rng);
      need[keys[ki]] = from[keys[ki]];
    }
    const want = t.tier === 1 ? 1 : 2;
    while (Object.keys(need).length < want) {
      let ai; let v;
      [ai, rng] = nextInt(ASPECTS.length, rng);
      [v, rng] = nextInt(2, rng);
      if (!(ASPECTS[ai] in need)) need = { ...need, [ASPECTS[ai]]: v === 1 };
    }
    reactions[id] = need;
  }
  return [{
    natures, reactions, experiments: [], known: {}, published: {}, debunked: [], settled: {},
  }, rng];
};

export const loreOf = (civ) => civ?.lore || null;
/** Write a patch into the book. */
export const patchLore = (civ, patch) => ({ ...civ, lore: { ...loreOf(civ), ...patch } });
export const natureOf = (civ, p) => loreOf(civ)?.natures?.[p];
export const knownOf = (civ, p) => loreOf(civ)?.known?.[p] || {};
export const settledOf = (civ, p) => loreOf(civ)?.settled?.[p];
export const publishedOf = (civ, p) => loreOf(civ)?.published?.[p];
export const reactionOf = (civ, techId) => loreOf(civ)?.reactions?.[techId] || {};
export const describeReaction = (need) => {
  const parts = ASPECTS.filter((k) => k in need).map((k) => aspectWord(k, need[k]));
  return parts.length ? joinWords(parts) : 'anything';
};
/** Does this mix's shared result satisfy the reaction? */
export const satisfies = (sharedResult, need) => Object.entries(need).every(([k, v]) => sharedResult[k] === v);

/** Mix two principles: what they share, from the hidden natures. */
export const mix = (civ, a, b) => shared(natureOf(civ, a), natureOf(civ, b));

/** A fact the world let slip: one aspect of one principle. */
/** The aspects known after that were not known before: [{ principle, aspect, value, said }]. */
export const revealsBetween = (before, after) => {
  const a = loreOf(before)?.known || {};
  const b = loreOf(after)?.known || {};
  const out = [];
  PRINCIPLES.forEach((p) => ASPECTS.forEach((aspect) => {
    if (b[p]?.[aspect] !== undefined && a[p]?.[aspect] === undefined) {
      out.push({ principle: p, aspect, value: b[p][aspect], said: `${PRINCIPLE_NAMES[p]} is ${aspectWord(aspect, b[p][aspect])}` });
    }
  }));
  return out;
};

export const revealAspect = (civ, principle, aspect) => {
  const book = loreOf(civ);
  if (!book || !PRINCIPLES.includes(principle) || !ASPECTS.includes(aspect)) return [civ, null];
  if (!book.natures?.[principle]) return [civ, null];
  if (book.known?.[principle]?.[aspect] !== undefined) return [civ, null];
  const value = book.natures[principle][aspect];
  const known = { ...book.known, [principle]: { ...(book.known[principle] || {}), [aspect]: value } };
  return [patchLore(civ, { known }), `${PRINCIPLE_NAMES[principle]} is ${aspectWord(aspect, value)}`];
};

/**
 * Every assignment of natures to principles consistent with what is known:
 * the experiments' results and the revealed aspects. 8! = 40320 to check;
 * fine. Returns { candidates: { principle: [natureKey...] }, count }.
 *
 * A FAILED bet counts too. An aimed experiment that missed records no
 * properties, but it does prove the pair cannot make that technology - so
 * every arrangement in which they WOULD have made it is out. Matt: "we
 * should go ahead and gray out the things we know it isn't." Anyone
 * tracking it on paper would do the same; the phone must not be dumber.
 */
export const consistentNatures = (lore) => {
  const book = lore;
  const P = PRINCIPLES;
  const experiments = book?.experiments || [];
  const known = book?.known || {};
  const settled = book?.settled || {};
  const reactions = lore?.reactions || {};
  const candidates = Object.fromEntries(P.map((p) => [p, new Set()]));
  let count = 0;
  const used = new Array(NATURES.length).fill(false);
  const assign = new Array(P.length).fill(null);

  const okSoFar = (i) => {
    const p = P[i];
    const n = NATURES[assign[i]];
    const k = known[p];
    if (k && ASPECTS.some((a) => k[a] !== undefined && k[a] !== n[a])) return false;
    const s = settled[p];
    if (s && !sameNature(s, n)) return false;
    for (const e of experiments) {
      const ia = P.indexOf(e.a);
      const ib = P.indexOf(e.b);
      if (ia < 0 || ib < 0 || ia > i || ib > i) continue;
      const got = shared(NATURES[assign[ia]], NATURES[assign[ib]]);
      if (e.failed) {
        const need = reactions[e.target];
        if (need && Object.keys(need).length && satisfies(got, need)) return false;
        continue;
      }
      if (ASPECTS.some((a) => (a in got) !== (a in e.shared) || (a in got && got[a] !== e.shared[a]))) return false;
    }
    return true;
  };

  const walk = (i) => {
    if (i === P.length) {
      count += 1;
      P.forEach((p, j) => candidates[p].add(natureKey(NATURES[assign[j]])));
      return;
    }
    for (let n = 0; n < NATURES.length; n += 1) {
      if (used[n]) continue;
      used[n] = true;
      assign[i] = n;
      if (okSoFar(i)) walk(i + 1);
      used[n] = false;
    }
  };
  walk(0);
  return { candidates: Object.fromEntries(P.map((p) => [p, [...candidates[p]]])), count };
};

/**
 * Every clue the notebook is reasoning from, each as a sentence, and each
 * with a way to take it back out again. The order is the order they were
 * learned in, which is the order a person would retell them.
 */
export const clueList = (lore) => {
  const out = [];
  const book = lore;
  const without = (l, patch) => ({ ...l, ...patch });
  (book?.experiments || []).forEach((e, i) => {
    const a = PRINCIPLE_NAMES[e.a];
    const b = PRINCIPLE_NAMES[e.b];
    if (e.failed) {
      out.push({
        id: `x${i}`,
        text: `${a} and ${b} did not make ${e.target}`,
        without: (l) => without(l, { experiments: l.experiments.filter((_, j) => j !== i) }),
      });
      return;
    }
    const what = Object.keys(e.shared || {}).length
      ? `share ${describeShared(e.shared)}, and nothing else`
      : 'share nothing at all';
    out.push({
      id: `x${i}`,
      text: `${a} and ${b} ${what}`,
      without: (l) => without(l, { experiments: l.experiments.filter((_, j) => j !== i) }),
    });
  });
  Object.entries(book?.known || {}).forEach(([p, byAspect]) => {
    Object.entries(byAspect || {}).forEach(([aspect, value]) => {
      out.push({
        id: `k${p}.${aspect}`,
        text: `the stones said ${PRINCIPLE_NAMES[p]} is ${aspectWord(aspect, value)}`,
        without: (l) => without(l, {
          known: { ...l.known, [p]: Object.fromEntries(Object.entries(l.known[p]).filter(([k]) => k !== aspect)) },
        }),
      });
    });
  });
  Object.keys(book?.settled || {}).forEach((p) => {
    out.push({
      id: `s${p}`,
      text: `${PRINCIPLE_NAMES[p]} is settled: ${describeNature(book.settled[p])}`,
      without: (l) => without(l, { settled: Object.fromEntries(Object.entries(l.settled).filter(([k]) => k !== p)) }),
    });
  });
  return out;
};

/**
 * WHY a property is certain. Runs the solver once without each clue in
 * turn: the clues it cannot do without are the argument, and that is the
 * shortest true story of how the notebook got there. Matt, 2026-09-20: "it
 * would be great if we could just in a paragraph explain how that new
 * deduction was arrived at... it would help people get a sense of how the
 * puzzle works."
 */
export const whyDeduced = (lore, principle, aspect) => {
  const sure = (l) => deducedAspects(l)[principle]?.[aspect] !== undefined;
  if (!sure(lore)) return null;
  const value = deducedAspects(lore)[principle][aspect];
  if (lore?.known?.[principle]?.[aspect] !== undefined) {
    return { value, told: true, needed: [] };
  }
  // Throw clues away one at a time, keeping only what the fact cannot
  // survive without. What is left is A shortest argument - not "everything
  // you know", which is true and useless, but the two or three lines
  // somebody could actually retell.
  let kept = lore;
  clueList(lore).forEach((orig) => {
    // Look the clue up again in what is LEFT: an experiment's `without`
    // works by position, and the positions move as the list shrinks.
    const here = clueList(kept).find((c) => c.text === orig.text);
    if (!here || here.fixed) return;
    const trimmed = here.without(kept);
    if (sure(trimmed)) kept = trimmed;
  });
  return { value, told: false, needed: clueList(kept).map((c) => c.text) };
};

/** Per principle, the aspects every consistent assignment agrees on. */
export const deducedAspects = (lore) => {
  const { candidates } = consistentNatures(lore);
  const out = {};
  PRINCIPLES.forEach((p) => {
    const ns = candidates[p].map((k) => NATURES.find((n) => natureKey(n) === k));
    out[p] = {};
    ASPECTS.forEach((a) => {
      if (ns.length && ns.every((n) => n[a] === ns[0][a])) out[p][a] = ns[0][a];
    });
  });
  return out;
};
